
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { usePlatformData } from '@/hooks/usePlatformData';
import { useToolFiltering } from '@/hooks/useToolFiltering';
import PlatformHeader from '@/components/platform/PlatformHeader';
import FilterSidebar from '@/components/platform/FilterSidebar';
import PlatformToolsList from '@/components/platform/PlatformToolsList';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import NoResults from '@/components/platform/NoResults';
import MetaTags from '@/components/MetaTags';

const PlatformPage = () => {
  const { slug } = useParams<{ slug: string }>();
  
  const [
    { platformName, platformSeoDescription, allTools, categories, sports, loading },
    { setFilteredTools }
  ] = usePlatformData(slug);
  
  const {
    selectedCategoryIds,
    selectedSportId,
    handleCategorySelect,
    handleSportSelect,
    filteredTools
  } = useToolFiltering({
    allTools,
    categories,
    sports
  });

  // Generate page title and create absolute URL for canonical reference
  const pageTitle = platformName ? `${platformName} Apps - FitnessAppSearch` : 'Loading Platform...';
  const pageDescription = platformSeoDescription || 
    `Discover the best fitness apps available for ${platformName?.toLowerCase() || 'all platforms'}.`;
  const canonicalUrl = slug ? `${window.location.origin}/platform/${slug}` : window.location.href;
  
  // Force meta tag refresh when platform name changes
  useEffect(() => {
    // This will help ensure og:url and other tags are set correctly
    if (platformName) {
      // Force a clear and reset of meta tags
      const metaElements = document.querySelectorAll('meta[property^="og:"], meta[name^="twitter:"]');
      metaElements.forEach(el => el.remove());
      
      // We'll let the MetaTags component re-add them
    }
  }, [platformName]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <MetaTags 
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={canonicalUrl}
        imageUrl={`${window.location.origin}/og-image.png`}
      />
      
      <Navigation />
      
      <main className="container mx-auto px-4 py-8 flex-grow">
        {platformName && <PlatformHeader platform={platformName} />}
        
        {loading ? (
          <LoadingSpinner />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Sidebar with filters */}
            <FilterSidebar
              categories={categories}
              sports={sports}
              selectedCategoryIds={selectedCategoryIds}
              selectedSportId={selectedSportId}
              onSelectCategory={handleCategorySelect}
              onSelectSport={handleSportSelect}
            />
            
            {/* Main content */}
            <div className="lg:col-span-3">
              {filteredTools.length > 0 ? (
                <PlatformToolsList 
                  platform={platformName} 
                  tools={filteredTools} 
                />
              ) : (
                <NoResults platform={platformName} />
              )}
            </div>
          </div>
        )}
      </main>
      
      <Footer />
    </div>
  );
};

export default PlatformPage;
